/**
 * Image presets — the closed set of sizes any storage image may be requested at.
 *
 * WHY A CLOSED SET (this is an economics constraint, not a style preference):
 *
 * - Every distinct `(width, height, quality, resize)` tuple is a SEPARATE cache
 *   entry for the same source file — height and crop mode count as much as
 *   width, so `TILE` and `CARD_SM` are two objects despite the near-equal
 *   width, and `HERO` and `CARD` are two despite the IDENTICAL one. On the
 *   Vercel optimizer the cache key is
 *   `projectId + w + q + url + normalized Accept`, and every MISS/STALE is
 *   billed as an image transformation **plus** a cache write. On the Supabase
 *   `render/image` path there is no Vercel bill, but the CDN still stores one
 *   object per parameter set and imgproxy still re-encodes on a cold key.
 * - The browser HTTP cache keys on the **exact URL string** and survives a full
 *   page navigation. So two screens that share a preset means the second screen
 *   paints for **0 bytes** — and two screens that differ by one character in
 *   `?quality=` pay for the same picture twice.
 *
 * Therefore: **the number of presets is the ceiling on how many cached copies
 * of one file can exist.** Every additional preset multiplies that ceiling for
 * every image in the catalogue, which is why this table is deliberately short
 * and coarse. A box that lands between two presets rounds UP to the larger one;
 * it does not get its own number. If a call site genuinely fits none of these,
 * that is a finding to raise, not a constant to add.
 *
 * WHY SIX RUNGS AND NOT TEN (the 2026-07-28 consolidation, and the reasoning
 * that has to survive the next person who wants "just one more size").
 *
 * The table used to carry FIVE square `cover` rungs in a row — 48, 96, 128, 192
 * and 256 device px — because they had been named after the surfaces that asked
 * for them (`AVATAR_XS/SM/MD`, `THUMB`, `THUMB_LG`) rather than after the boxes
 * they cover. Five names, one shape, adjacent numbers. Measured on two real prod
 * objects (a listing photo and an avatar, `render/image` at `q60`), the bytes
 * between neighbouring rungs are:
 *
 * | step        | listing photo | avatar   |
 * |-------------|---------------|----------|
 * | 48  -> 128  | +938 B        | +934 B   |
 * | 96  -> 128  | +430 B        | +432 B   |
 * | 192 -> 256  | +1124 B       | +582 B   |
 *
 * That is the whole price of a merge: under a kilobyte per picture. What it buys
 * is that the profile header, the chat header, the order-line photo, the offer
 * row, the cart row, the listing-detail thumbnail strip and the feed's ambient
 * halo now all request the SAME two URLs for the same file. A user who saw a
 * face at 32 px in an inbox row paints it for 0 bytes in the profile hero; a
 * listing photo fetched once for a cart row is already warm when the same photo
 * turns up as a saved-item tile. Fewer, coarser rungs beat exactly-sized ones
 * whenever surfaces overlap, and on a marketplace they overlap constantly.
 *
 * 128 and 256 specifically, rather than any other pair: they are the two rungs
 * the busiest surfaces were ALREADY on. Keeping their `(width, height, quality)`
 * byte-identical means the merge does not orphan a single warm CDN entry for the
 * two highest-traffic tiers — only the avatar and halo URLs re-key, and those
 * re-key onto an object the catalogue is already storing. Picking 96/192 instead
 * would have saved bytes and left the 72 px rows 33 % short and the 100-110 px
 * tiles 4-10 % short; picking 144/288 would have covered every box exactly and
 * re-keyed the entire catalogue to buy sharpness nobody asked for.
 *
 * The bar that a NEW rung has to clear, stated once so it can be applied
 * consistently: it is not "no existing preset is a perfect fit" (nothing ever
 * is) but **"every existing preset makes the picture visibly worse, and the
 * surface is rendered often enough for that to matter"**. `TILE` and `HERO`
 * cleared it — see their comments. Both are `cover` rungs, and they exist
 * because before them the table had no way to describe a NON-SQUARE box at all:
 * every rung was either a square crop or a width-only `contain` downscale, and
 * in a fixed-aspect box the latter upscales 1.25x-2.7x for every source shape
 * whose cover fit is height-driven. Rounding a box up to a bigger existing rung
 * costs bytes; asking for a rung that cannot cover the box costs sharpness, and
 * only the second one is a defect.
 *
 * WHY THIS FILE AND NOT NEXT TO THE BUILDER: `lib/image-thumbnails.ts` is the
 * *mechanism* (how to rewrite a public URL into a render URL) and has not
 * changed since it was written. These presets are *policy* — they move whenever
 * the design does. Different rates of change, different files. The dependency
 * runs policy -> mechanism and never the reverse.
 *
 * THE `resize` / `height` INVARIANT: the builder forces `resize=contain`
 * whenever `height` is absent, because the render endpoint under `resize=cover`
 * with a single dimension fills `width x ORIGINAL height` and crops the sides to
 * a vertical sliver (see the comment at `lib/image-thumbnails.ts:63`). A preset
 * that declared `cover` without a `height` would therefore describe a URL the
 * builder never emits. That combination is unrepresentable below: `cover`
 * requires `height`.
 *
 * THE `quality` FIELD IS A SUPABASE PARAMETER, NOT A NEXT ONE — and the
 * difference is load-bearing. `imagePresetUrl` emits a Supabase `render/image`
 * URL, whose `quality` imgproxy accepts at any value; that is why the square
 * rungs can sit at 60 while the large ones sit at 75. The Vercel optimizer does
 * NOT: measured against prod on 2026-07-28, `/_next/image` answers **only**
 * `q=75` and returns **HTTP 400** for `q=60` and `q=100`, because Next 16
 * defaults `images.qualities` to `[75]` and `next.config.ts` does not widen it.
 * The two never meet only because a preset URL is always rendered with
 * `unoptimized` (it is already the exact size; handing it to the optimizer buys
 * a second server hop for identical pixels), a rule pinned by
 * `tests/unit/fitness-next-image-params.test.ts`. If a preset URL ever loses
 * `unoptimized` while carrying its own `quality={60}`, the symptom is a 400 on
 * every image, not a soft picture.
 *
 * @see lib/image-thumbnails.ts — the mechanism this file parameterises
 * @see apps/mobile/lib/image-presets.ts — the mobile mirror of this table (the
 *      Supabase CDN edge cache is shared between clients, so a matching
 *      parameter tuple on both platforms means each warms the other's cache).
 *      Edit one, edit both — including the UNITS table in the mobile header.
 * @see tests/unit/fitness-image-presets-mobile-parity.test.ts — the pin that
 *      fails CI when the two tables (or the rung COUNT) drift apart
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §2, §8.2
 */

import { toSupabasePublicImageThumbUrl } from "@/lib/image-thumbnails"
import type { ImageRefInput } from "@/lib/storage/public-url"

/**
 * A box crop: the CDN returns exactly `width x height`, so the CSS
 * `object-cover` has nothing left to do and we ship the fewest possible bytes.
 * `height` is REQUIRED — see the invariant in the file header.
 */
type CoverPreset = Readonly<{
  resize: "cover"
  width: number
  height: number
  quality: number
}>

/**
 * An aspect-preserving downscale. `height`, when given, is an upper bound the
 * image is fitted inside rather than cropped to; most presets omit it.
 */
type ContainPreset = Readonly<{
  resize: "contain"
  width: number
  height?: number
  quality: number
}>

/**
 * All widths/heights are **device pixels** (what the CDN is asked for), not CSS
 * pixels. The "intended box" in each preset's doc comment is CSS px at 2x DPR.
 */
export type ImagePreset = CoverPreset | ContainPreset

/**
 * The small square crop — every square box up to **64 CSS px**.
 *
 * NAMED FOR THE BOX, NOT THE SURFACE, and that is the point of the name. This
 * one rung serves avatars (inline bylines, list rows, chat headers, the 64 px
 * profile chip), the listing-detail thumbnail strip, order and offer lines, cart
 * rows, chat compose chips, my-listings rows, the order article-preview mosaic
 * AND the blurred ambient halo behind a feed card. It absorbed `AVATAR_XS` (48),
 * `AVATAR_SM` (96), `THUMB` (128) and `AMBIENT` (64, `contain`, q40). Calling
 * any of that "AVATAR_SM" was a lie the moment the second non-avatar surface
 * imported it, and a name that lies is how a table grows a duplicate rung: the
 * next person needing a 40 px product chip reads "AVATAR" and adds `THUMB_XS`.
 *
 * 128 rather than 96: the listing-detail thumbnail strip renders 64 CSS px
 * squares (measured at 22 844 B for nine of them) and is the single busiest
 * consumer, so the rung lands ON it instead of just beside it. The 72 px rows
 * (my-listings, offer cards, the order mosaic) sit ~11 % above and round DOWN on
 * purpose — `SQUARE_LG` is 4x the pixels to hide an upscale nobody can see on a
 * decorative row. The 16-24 px chips (order-card seller badge, chat compose
 * avatar) are over-served by 5-8x and that is also deliberate: they are almost
 * always the same face already on screen at 32-48 px, so the oversize fetch is
 * a cache HIT, while a dedicated tiny rung would be a second stored object per
 * face to save ~900 B once.
 *
 * THE AMBIENT HALO IS HERE ON PURPOSE. `AMBIENT` used to be its own rung — 64 px
 * wide, `contain`, quality 40 — on the argument that a `blur-2xl` destroys every
 * frequency larger pixels would carry. True, and still true; it just was not
 * worth a rung. All three consumers
 * (`components/feed/marketplace-vertical-snap-feed.tsx` x2,
 * `components/search/product-card-carousel.tsx`) paint it as a
 * `background-image` with `background-size: cover` into a ~0.6-aspect box under
 * `blur-2xl` at `opacity-20` (or `saturate-125 brightness-90`), so the browser
 * was ALREADY cropping the source — moving that crop to the CDN changes which
 * band of the photo survives, not whether one does, and at a 40 px blur radius
 * and 20 % opacity the halo is a colour wash either way. Measured cost of the
 * merge: +888 B / +812 B per halo on the two prod objects above. Measured
 * benefit besides the deleted rung: the halo now re-uses whatever square
 * thumbnail of that same listing the cart / saved / order surfaces already
 * warmed, and its upscale into the halo box drops from ~7.5x to ~4.7x.
 *
 * @see squarePresetFor — the box -> rung ladder for callers holding a number
 */
export const SQUARE_SM = {
  resize: "cover",
  width: 128,
  height: 128,
  quality: 60,
} as const satisfies ImagePreset

/**
 * The large square crop — every square box from 65 to **128 CSS px**.
 *
 * Chat listing pickers, the saved-listing preview tile, the post-review grid,
 * the profile hero and edit avatars, the collection mosaics. It absorbed
 * `AVATAR_MD` (192) and `THUMB_LG` (256).
 *
 * 256 and not 192, even though 192 covered every avatar box that used to ask for
 * it: the tile surfaces on this rung run 96-128 CSS px (chat picker grids,
 * `Chat.tsx`'s desktop 128 px preview, mobile `ItemRow` md at 100 pt), and 192
 * would leave the widest of them 25 % short. 256 covers 128 CSS px at exactly 2x
 * and is the number the tile surfaces were already on, so no warm entry is
 * orphaned. The two boxes that used to overshoot this rung's predecessor —
 * `profile-edit-form`'s `md:h-28` (112 CSS px, previously served at 1.71x DPR)
 * and `search/user-card`'s `sm:w-14` (56 px, served at 1.71x by the old 96 rung)
 * — are now covered at >=2x, i.e. the merge made them SHARPER, not softer.
 *
 * Above 128 CSS px nothing covers: `squarePresetFor` clamps here rather than
 * inventing a seventh rung, and two live sites sit just past the clamp —
 * `components/chat/ChatListingBottomSheet.tsx` (grid cell in an uncapped desktop
 * sheet, ~140 CSS px) and the mobile `chat-listing-picker-tile` on >=430 pt
 * phones (~130-133 pt). Both were already short before this consolidation; both
 * are findings against this table, not numbers to add to it.
 */
export const SQUARE_LG = {
  resize: "cover",
  width: 256,
  height: 256,
  quality: 60,
} as const satisfies ImagePreset

/**
 * Small listing card / compact grid — dense multi-column layouts, and the
 * `object-contain` escape hatch.
 *
 * The second job is the one that is easy to lose: this is a width-only
 * `contain` downscale, so it is what a NON-square or letterboxed box must ask
 * for. `checkout/order-placed.tsx`, `cart/cart-item.tsx`, `cart/order-item.tsx`,
 * `cart/items-ordered.tsx` and `orders/order-listing-card.tsx` all render
 * `object-contain` and all deliberately sit here rather than on a square rung —
 * a server-side square crop under `object-contain` would letterbox an
 * ALREADY-cropped picture and cut the top and bottom off portrait photos. Same
 * for `chat-message.tsx`'s single-image bubble (`w-40 aspect-3/4`).
 *
 * A square `cover` rung under `object-contain` is the one substitution this
 * table cannot absorb. If a surface needs both a fixed aspect AND a crop, it
 * wants `TILE`, not this.
 */
export const CARD_SM = {
  resize: "contain",
  width: 384,
  quality: 75,
} as const satisfies ImagePreset

/**
 * The fixed **4:5** listing tile — the grid cell and the horizontal strip card,
 * on BOTH platforms (`components/search/item-card.tsx` `aspect-4/5`,
 * `apps/mobile/components/ui/item-card.tsx` `aspectRatio: 4 / 5`).
 *
 * THE FIRST NON-SQUARE COVER RUNG, and why one was needed: the alternatives are
 * a square crop or a width-only `contain` downscale, and a `contain` request is
 * pixel-exact only while the box's cover fit is WIDTH-driven — i.e. only while
 * the source is 4:5 or narrower. A phone camera held upright gives 3:4 and is
 * fine; a **square** upload is height-driven in this box and gets upscaled
 * ~1.25x, a **4:3 landscape** upload ~1.67x. That is a visible softness tax on
 * the single most-rendered image in the product, paid to keep the URL count
 * down. Declaring the box instead buys both: the CDN returns exactly 512x640,
 * the client `object-cover` / `contentFit="cover"` has nothing left to crop, and
 * it is still ONE url for every device.
 *
 * WHY 512 AND NOT 384 (the `CARD_SM` width this replaced on the tile surfaces).
 * 512 is the smallest rung that covers the largest live 4:5 box on EITHER
 * platform, which is the whole point of a mirrored table — measured, not
 * assumed:
 *
 * | surface                                   | box (CSS px / pt) | needs @2x |
 * |-------------------------------------------|-------------------|-----------|
 * | web 2-col grid, 390 viewport              | ~171              | 342       |
 * | web 3-col grid, 768 viewport              | ~202              | 404       |
 * | web 5-col discover panel, 1440 viewport   | ~262              | 524       |
 * | web 4-col grid, >=1400 viewport (widest)  | ~295              | 590       |
 * | mobile strip card                         | 148-150           | 296-300   |
 * | mobile half-window grid cell              | 170-220           | 340-440   |
 *
 * So 512 is a DOWNSCALE everywhere except the widest desktop column, where it
 * is 15 % short — the same order as the 8 % shortfall `CARD` already accepts,
 * and an order below the 25-67 % upscales it removes. The alternative numbers
 * both fail one side: 384 leaves that desktop column 54 % short, 640 makes the
 * mobile feed grid fetch 2.8x the pixels its cell can show, on cellular.
 *
 * The mobile mirror is `256 x 320` pt, which is the same 512x640 device px at
 * the DPR>=2 cap — so whichever client renders a listing first warms the other.
 *
 * @see apps/mobile/lib/image-presets.ts `TILE`
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §8.2
 */
export const TILE = {
  resize: "cover",
  width: 512,
  height: 640,
  quality: 75,
} as const satisfies ImagePreset

/**
 * The listing card, the listing-detail slide AND the listing-detail blurred
 * backdrop — deliberately ONE preset, so all three collapse into a single
 * browser-cache entry (and a single Supabase CDN entry) instead of three.
 *
 * This is why the number lives here rather than as a literal in each component:
 * the browser HTTP cache keys on the exact URL string and survives a full page
 * navigation, so a feed -> detail navigation paints the main photo and its
 * backdrop from cache for 0 bytes. String identity IS the whole optimisation —
 * one differing character and it is a fresh ~33 KB download with no runtime
 * symptom whatsoever. Both sides importing the same declaration is what stops
 * the match rotting when someone edits one number.
 *
 * Why 828 and not 1080: a stored listing photo is ~1080 px on its long edge
 * (device-side compression at upload), and the slide renders `object-contain`
 * in an `aspect-4/5` box ~640 px wide at a 1440 viewport. A portrait source is
 * therefore HEIGHT-limited and displays at ~450 CSS px, i.e. ~900 px at 2x DPR —
 * 828 is 8 % short, while 1080 was 20 % over. For square/landscape sources the
 * box is width-limited and wants ~1280 px, which NEITHER value reaches because
 * the source itself is only 1080 wide. The extra 250 px never closed the gap
 * where one existed, and was waste where none did.
 *
 * @see components/ui/listing-image.tsx — the card side
 * @see lib/listings/gallery-image-urls.ts — the slide + backdrop side
 * @see tests/unit/listing-gallery-image-urls.test.ts — the pin
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §12.1
 */
export const CARD = {
  resize: "contain",
  width: 828,
  quality: 75,
} as const satisfies ImagePreset

/**
 * The full-bleed feed hero — ONE photo filling the entire phone screen
 * (`apps/mobile/components/feed-snap-card.tsx`).
 *
 * The second non-square cover rung, for the same reason as `TILE` and with a
 * worse untreated case: a 0.46-aspect box is so much taller than any upload
 * that a width-only `contain` request is height-driven for *every* source
 * shape. Measured against a 1080x1920 stored photo in an 844 pt tall slide:
 * `CARD` (828 wide, contain) upscales a 9:16 source 1.15x, a 3:4 source 1.53x
 * and a 4:3 landscape source **2.7x**. There is no existing rung that avoids
 * that, because avoiding it requires declaring the HEIGHT.
 *
 * 828 x 1792 device px = 414 x 896 pt = a 0.462 box, which is the phone screen
 * to within a couple of percent across the install base — 390x844 = 0.462,
 * 393x852 = 0.461, 412x915 = 0.450, 430x932 = 0.461. The one outlier is the SE
 * (375x667 = 0.562), and it is WIDTH-driven against this rung (375 pt box vs a
 * 414 pt rung), so it crops rather than stretches. Worst case anywhere on a
 * phone is a ~1.07x scale; a tablet in the feed is a genuine upscale and is
 * accepted rather than given a rung of its own.
 *
 * Deliberately NOT the measured `(slideWidth, slideHeight)` it replaced: those
 * fold the safe area and the tab-bar inset in on top of the screen size, so
 * every device model minted its own CDN object, its own imgproxy encode and its
 * own expo-image disk entry for **every photo scrolled past in the feed**. The
 * closed set exists precisely to cap that count.
 *
 * The width is `CARD`'s 828 on purpose — same column of source pixels, so the
 * two rungs cost imgproxy the same decode — but the URLs are distinct (height +
 * `resize=cover`), which is correct: a letterboxed slide and a cropped hero are
 * not the same picture.
 *
 * NO WEB CONSUMER TODAY, and that is not an oversight. Web's vertical snap feed
 * renders the photo `object-contain` (letterboxed, over a `SQUARE_SM` blur), so
 * it wants `CARD`. The rung is declared here anyway so the two tables stay ONE
 * table: a constant nobody requests costs zero cache entries, whereas a web hero
 * added later without it would mint a fresh number. It is the only rung that is
 * now one-sided in that way — `AMBIENT` used to be the mirror image (declared on
 * mobile, consumed only on web) and was folded into `SQUARE_SM`.
 *
 * @see apps/mobile/lib/image-presets.ts `HERO`
 */
export const HERO = {
  resize: "cover",
  width: 828,
  height: 1792,
  quality: 75,
} as const satisfies ImagePreset

/**
 * Box (CSS px) -> square rung, snapping UP onto the closed set.
 *
 * This is where a free number stops. It lives HERE, next to the table it
 * selects from, rather than inside whichever component happens to need it first
 * — a second component reaching for "which rung covers a 40 px circle?" would
 * otherwise copy the ladder, and two ladders drift into two different answers
 * for the same box, which is exactly the fragmentation the closed set exists to
 * prevent. Mirrored in `apps/mobile/lib/image-presets.ts`, where the phone's
 * `<Avatar size={38}>` resolves its box at RUNTIME and so cannot pick a rung any
 * other way.
 *
 * Named for the SHAPE it selects, not for avatars: it was `avatarPresetFor` when
 * the rungs were `AVATAR_*`, and both names were wrong the moment the same rungs
 * started serving order lines and thumbnail strips. Any caller holding a square
 * box and a number belongs here.
 *
 * Deliberately a ladder and not a policy engine: two comparisons, no options, no
 * caller-supplied thresholds. Above `SQUARE_LG` it clamps rather than inventing
 * a third rung — a genuinely larger square box is a finding to raise against
 * this table, not a number to add here.
 *
 * Units: preset widths are DEVICE px and the box they cover is half that at the
 * 2x DPR this table is written for (see the type comment above), which is why
 * the comparison halves rather than comparing to `width` directly.
 *
 * The web `<Avatar>` primitive does NOT call this — its box is a Tailwind class
 * (`h-8 w-8`), decided at build time, so the equivalent check is build-time too:
 * `tests/unit/fitness-avatar-preset-covers-box.test.ts` fails a box that
 * outgrew the default rung without saying so. This export is the runtime half of
 * the same rule, for any caller that has a number instead of a class.
 */
export function squarePresetFor(boxCssPx: number): ImagePreset {
  if (boxCssPx <= SQUARE_SM.width / 2) return SQUARE_SM
  return SQUARE_LG
}

/**
 * The ONE call shape for a preset-sized storage image.
 *
 * Every consumer looks identical through this function, which is what makes the
 * planned fitness gate possible: "a `toSupabasePublicImageThumbUrl` call with a
 * numeric literal instead of a preset" becomes a greppable violation
 * (handoff §10). It is a pure forward to the builder — no policy of its own.
 *
 * Passes `blob:` / `data:` / `/local.svg` / non-storage refs through untouched
 * (the builder does), so HEIC blobs and placeholders are safe to hand in.
 */
export function imagePresetUrl(raw: ImageRefInput, preset: ImagePreset): string {
  return toSupabasePublicImageThumbUrl(
    raw,
    preset.width,
    preset.quality,
    preset.resize,
    preset.height,
  )
}
